import { useEffect, useReducer } from 'react'
import { Alert, Button, Spin } from 'antd'
import { useToast } from '@/contexts/ToastContext'
import { useAuthSession } from '@/lib/session'
import { productThemes } from '@/lib/productBranding'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { usePlatformConnections } from '@/features/platform/hooks'
import { useSaveCapacitySettings } from '../hooks'
import {
  DataSource,
  StaffAssignedDataPoint,
  type CapacitySettings,
  type BudgetMode,
  type Granularity,
} from '../types'

interface Props {
  onComplete: () => void | Promise<void>
  initialSettings?: CapacitySettings | null
  mode?: 'setup' | 'settings'
  onCancel?: () => void
}

interface SetupState {
  dataSource: DataSource | null
  staffAssignedDataPoint: StaffAssignedDataPoint | null
  budgetMode: BudgetMode
  granularity: Granularity
}

type SetupAction =
  | { type: 'SET_DATASOURCE'; value: DataSource }
  | { type: 'CLEAR_DATASOURCE' }
  | { type: 'SET_STAFF_ASSIGNED_DATA_POINT'; value: StaffAssignedDataPoint | null }
  | { type: 'SET_BUDGET_MODE'; value: BudgetMode }
  | { type: 'SET_GRANULARITY'; value: Granularity }

function reducer(state: SetupState, action: SetupAction): SetupState {
  switch (action.type) {
    case 'SET_DATASOURCE': {
      const options = getAllowedStaffAssignedOptions(action.value)
      const currentIsValid = options.some((option) => option.value === state.staffAssignedDataPoint)
      return {
        ...state,
        dataSource: action.value,
        staffAssignedDataPoint: currentIsValid ? state.staffAssignedDataPoint : getDefaultStaffAssignedDataPoint(action.value),
      }
    }
    case 'CLEAR_DATASOURCE': return { ...state, dataSource: null, staffAssignedDataPoint: null }
    case 'SET_STAFF_ASSIGNED_DATA_POINT': return { ...state, staffAssignedDataPoint: action.value }
    case 'SET_BUDGET_MODE': return { ...state, budgetMode: action.value }
    case 'SET_GRANULARITY': return { ...state, granularity: action.value }
    default: return state
  }
}

const DATA_SOURCES: { source: DataSource; label: string; abbr: string; sub: string }[] = [
  { source: DataSource.XpmJob,     label: 'Xpm Job',      abbr: 'XPM',   sub: 'Xero Practice Manager' },
  { source: DataSource.XpmJobTask, label: 'Xpm Job-Task', abbr: 'XPM', sub: 'Xero Practice Manager' },
  { source: DataSource.FYI,        label: 'FYI',          abbr: 'FYI',   sub: 'FYI Elite Jobs' },
  // { source: DataSource.Karbon,     label: 'Karbon',       abbr: 'K',     sub: 'Karbon Practice Mgmt' },
]

const BUDGET_MODES: { value: BudgetMode; label: string; desc: string }[] = [
  { value: 'hours', label: 'Hours', desc: 'Track capacity in billable hours' },
  { value: 'dollars', label: 'Dollars Budget', desc: 'Track capacity in revenue value' },
]

const GRANULARITIES: { value: Granularity; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
]

const STAFF_ASSIGNED_OPTIONS: Record<DataSource, { value: StaffAssignedDataPoint; label: string; desc: string }[]> = {
  [DataSource.XpmJob]: [
    { value: StaffAssignedDataPoint.XpmJobManager, label: 'XPM Job Manager', desc: 'Use XPM job manager' },
    { value: StaffAssignedDataPoint.XpmJobStaff, label: 'XPM Job Staff', desc: 'Use XPM job staff' },
    { value: StaffAssignedDataPoint.DashboardInsightsOnly, label: 'Dashboard Insights only', desc: 'Use planner allocations only' },
  ],
  [DataSource.XpmJobTask]: [
    { value: StaffAssignedDataPoint.XpmJobManager, label: 'XPM Job Manager', desc: 'Use parent XPM job manager' },
    { value: StaffAssignedDataPoint.XpmJobTaskStaff, label: 'XPM Job Task Staff', desc: 'Use XPM task assigned staff' },
    { value: StaffAssignedDataPoint.DashboardInsightsOnly, label: 'Dashboard Insights only', desc: 'Use planner allocations only' },
  ],
  [DataSource.FYI]: [
    { value: StaffAssignedDataPoint.FyiJobManager, label: 'FYI Job Manager', desc: 'Use FYI job manager' },
    { value: StaffAssignedDataPoint.DashboardInsightsOnly, label: 'Dashboard Insights only', desc: 'Use planner allocations only' },
  ],
  [DataSource.Karbon]: [
    { value: StaffAssignedDataPoint.DashboardInsightsOnly, label: 'Dashboard Insights only', desc: 'Use planner allocations only' },
  ],
}

function getAllowedStaffAssignedOptions(dataSource: DataSource | null) {
  return dataSource ? STAFF_ASSIGNED_OPTIONS[dataSource] ?? [] : []
}

function getDefaultStaffAssignedDataPoint(dataSource: DataSource | null) {
  return getAllowedStaffAssignedOptions(dataSource)[0]?.value ?? null
}

function isValidStaffAssignedDataPoint(
  dataSource: DataSource | null,
  value: StaffAssignedDataPoint | null | undefined
) {
  if (!dataSource || value == null) {
    return false
  }

  return getAllowedStaffAssignedOptions(dataSource).some((option) => option.value === value)
}

function getInitialStaffAssignedDataPoint(settings: CapacitySettings | null) {
  const dataSource = settings?.dataSource ?? null
  const savedValue = settings?.staffAssignedDataPoint
  return isValidStaffAssignedDataPoint(dataSource, savedValue)
    ? savedValue ?? null
    : getDefaultStaffAssignedDataPoint(dataSource)
}

function getStaffAssignmentSyncNote(
  dataSource: DataSource | null,
  staffAssignedDataPoint: StaffAssignedDataPoint | null
) {
  if (!dataSource || staffAssignedDataPoint == null) {
    return null
  }

  if (dataSource === DataSource.XpmJob) {
    if (staffAssignedDataPoint === StaffAssignedDataPoint.XpmJobManager) {
      return 'XPM sync enabled: allocation saves and removals will sync the XPM job manager. Only one manager can be assigned per job.'
    }

    if (staffAssignedDataPoint === StaffAssignedDataPoint.XpmJobStaff) {
      return 'XPM sync enabled: allocation saves and removals will sync XPM job staff.'
    }
  }

  if (dataSource === DataSource.XpmJobTask) {
    if (staffAssignedDataPoint === StaffAssignedDataPoint.XpmJobManager) {
      return 'XPM sync enabled: allocation saves and removals will sync the parent XPM job manager. Only one manager can be assigned per job/task.'
    }

    if (staffAssignedDataPoint === StaffAssignedDataPoint.XpmJobTaskStaff) {
      return 'XPM sync enabled: allocation saves and removals will sync assigned staff on the matching XPM job task.'
    }
  }

  if (dataSource === DataSource.FYI && staffAssignedDataPoint === StaffAssignedDataPoint.FyiJobManager) {
    return 'FYI sync enabled: allocation saves and removals will sync the FYI job manager.'
  }

  if (staffAssignedDataPoint === StaffAssignedDataPoint.DashboardInsightsOnly) {
    return 'Dashboard Insights only: allocation saves and removals will not sync assigned staff back to the connected platform.'
  }

  return null
}

function getBudgetModeSyncNote(
  dataSource: DataSource | null,
  staffAssignedDataPoint: StaffAssignedDataPoint | null,
  budgetMode: BudgetMode
) {
  if (!dataSource || staffAssignedDataPoint == null) {
    return null
  }

  if (staffAssignedDataPoint === StaffAssignedDataPoint.DashboardInsightsOnly) {
    let platform = ''
    if (dataSource === DataSource.XpmJob || dataSource === DataSource.XpmJobTask) {
      platform = 'XPM'
    } else if (dataSource === DataSource.FYI) {
      platform = 'FYI'
    }
    return `${platform} sync disabled: budget saves are stored in Dashboard Insights only and will not sync back to the connected platform.`
  }

  if (dataSource === DataSource.FYI && staffAssignedDataPoint === StaffAssignedDataPoint.FyiJobManager) {
    return 'FYI sync enabled: budget saves will sync FYI job budgets.'
  }

  if (dataSource === DataSource.XpmJob) {
    return budgetMode === 'dollars'
      ? 'XPM sync enabled: budget saves will sync XPM job dollar budgets.'
      : 'XPM sync disabled: hour budget saves are stored in Dashboard Insights only and will not sync to XPM jobs.'
  }

  if (dataSource === DataSource.XpmJobTask) {
    return budgetMode === 'hours'
      ? 'XPM sync enabled: budget saves will sync XPM task estimated minutes.'
      : 'XPM sync disabled: dollar budget saves are stored in Dashboard Insights only and will not sync to XPM tasks.'
  }

  return 'Sync disabled: budget saves are stored in Dashboard Insights only and will not sync back to the connected platform.'
}

function getBudgetModeIcon(mode: BudgetMode) {
  if (mode === 'hours') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    )
  }

  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2v20" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  )
}

function SyncNote({ note }: { note: string }) {
  return (
    <div className="mt-2 rounded-lg border border-cyan-100 bg-cyan-50 px-3 py-2 text-[12px] font-medium leading-relaxed text-cyan-900">
      {note}
    </div>
  )
}

export function SetupPage({ onComplete, initialSettings = null, mode = 'setup', onCancel }: Props) {
  const session = useAuthSession()
  const clientId = session?.currentClientId ?? ''
  const { addToast } = useToast()
  const plannerTheme = productThemes.pracPlanner
  const saveMutation = useSaveCapacitySettings()
  const {
    data: platformConnections = [],
    isLoading: isPlatformLoading,
    isError: isPlatformError,
    refetch: refetchPlatforms,
  } = usePlatformConnections({ clientId })

  const [state, dispatch] = useReducer(reducer, {
    dataSource: initialSettings?.dataSource ?? null,
    staffAssignedDataPoint: getInitialStaffAssignedDataPoint(initialSettings),
    budgetMode: initialSettings?.budgetMode ?? 'hours',
    granularity: initialSettings?.defaultGranularity ?? 'weekly',
  })

  const hasValidPlatformStatus = (status: string, lastSuccessfulRefresh: string | null) =>
    status === 'Refreshed' || (status === 'Failed' && Boolean(lastSuccessfulRefresh))
  const hasXpm = platformConnections.some((platform) =>
    platform.type === 'XPM'
    && hasValidPlatformStatus(platform.status, platform.lastSuccessfulRefresh)
    && platform.isAuthenticated
    && platform.hasRefreshToken
  )
  const hasFyi = platformConnections.some((platform) =>
    platform.type === 'FYI'
    && hasValidPlatformStatus(platform.status, platform.lastSuccessfulRefresh)
    && platform.hasApiKey
    && platform.hasApiSecret
  )
  const availableDataSources = DATA_SOURCES.filter(({ source }) =>
    source === DataSource.FYI ? hasFyi : hasXpm
  )
  const isAvailableDataSource = state.dataSource != null
    && availableDataSources.some(({ source }) => source === state.dataSource)

  useEffect(() => {
    if (isPlatformLoading || isPlatformError || state.dataSource == null || isAvailableDataSource) {
      return
    }

    dispatch({ type: 'CLEAR_DATASOURCE' })
  }, [isAvailableDataSource, isPlatformError, isPlatformLoading, state.dataSource])

  const availableStaffAssignedOptions = getAllowedStaffAssignedOptions(state.dataSource)
  const canSave = !isPlatformLoading
    && !isPlatformError
    && isAvailableDataSource
    && isValidStaffAssignedDataPoint(state.dataSource, state.staffAssignedDataPoint)
  const staffAssignmentSyncNote = getStaffAssignmentSyncNote(state.dataSource, state.staffAssignedDataPoint)
  const budgetModeSyncNote = getBudgetModeSyncNote(state.dataSource, state.staffAssignedDataPoint, state.budgetMode)

  async function handleLaunch() {
    if (!state.dataSource || state.staffAssignedDataPoint == null) return
    try {
      await saveMutation.mutateAsync({
        clientId,
        payload: {
          dataSource: state.dataSource,
          staffAssignedDataPoint: state.staffAssignedDataPoint,
          budgetMode: state.budgetMode,
          defaultGranularity: state.granularity,
          targetUtilisationPct: initialSettings?.targetUtilisationPct ?? 80,
        },
      })
      addToast(mode === 'settings' ? 'PracPlanner settings saved' : 'PracPlanner configured successfully', 'success')
      await onComplete()
    } catch {
      addToast('Failed to save settings', 'error')
    }
  }

  if (isPlatformLoading) {
    return (
      <FeatureThemeProvider featureKey="pracPlanner" withAntd className="flex justify-center p-12">
        <Spin size="large" />
      </FeatureThemeProvider>
    )
  }

  if (isPlatformError) {
    return (
      <FeatureThemeProvider featureKey="pracPlanner" withAntd className="p-6">
        <Alert
          type="error"
          message="Failed to load connected platforms"
          action={<Button size="small" onClick={() => refetchPlatforms()}>Retry</Button>}
        />
      </FeatureThemeProvider>
    )
  }

  return (
    <FeatureThemeProvider featureKey="pracPlanner" className="min-h-screen flex justify-center">
      <div className="bg-white rounded-2xl shadow-lg max-w-[640px] w-full overflow-hidden border" style={{ borderColor: plannerTheme.border }}>

        {/* Header */}
        <div className="px-10 pt-8 pb-6 text-center border-b border-gray-100">
          <div className="w-14 h-14 rounded-[14px] inline-flex items-center justify-center mb-4" style={{ backgroundColor: plannerTheme.primary }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="9" rx="1" />
              <rect x="14" y="3" width="7" height="5" rx="1" />
              <rect x="14" y="12" width="7" height="9" rx="1" />
              <rect x="3" y="16" width="7" height="5" rx="1" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {mode === 'settings' ? 'PracPlanner Settings' : 'PracPlanner'}
          </h1>
          <p className="text-sm text-gray-400">
            {mode === 'settings'
              ? 'Update your practice management system and planning defaults'
              : 'Connect your practice management system to get started'}
          </p>
        </div>

        {/* Body - all steps visible at once */}
        <div className="px-10 py-8 space-y-8">

          {/* Step 1: Data Source */}
          <div>
            <div className="flex items-center mb-3">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold mr-2.5 shrink-0" style={{ backgroundColor: plannerTheme.primary }}>1</span>
              <span className="text-[15px] font-bold text-gray-900">Connect Your Data Source</span>
            </div>
            <p className="text-[13px] text-gray-400 mb-3 pl-[34px]">Select the system where you manage jobs and timesheets.</p>
            <div className="flex gap-3 pl-[34px]">
              {availableDataSources.map(({ source, label, abbr, sub }) => {
                const selected = state.dataSource === source
                return (
                  <button
                    key={source}
                    onClick={() => dispatch({ type: 'SET_DATASOURCE', value: source })}
                    className={`border-2 rounded-[10px] p-4 text-center transition-all flex-1 ${
                      selected
                        ? ''
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    style={selected ? { borderColor: plannerTheme.primary, backgroundColor: plannerTheme.soft } : undefined}
                  >
                    <div className={`w-11 h-11 rounded-[10px] flex items-center justify-center mx-auto mb-2 text-[15px] font-bold ${
                      selected ? '' : 'bg-gray-100 text-gray-600'
                    }`} style={selected ? { backgroundColor: plannerTheme.tagBackground, color: plannerTheme.primary } : undefined}>
                      {abbr}
                    </div>
                    <div className="text-[13px] font-semibold text-gray-700">{label}</div>
                    <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>
                  </button>
                )
              })}
            </div>
            {availableDataSources.length === 0 ? (
              <p className="mt-3 pl-[34px] text-[13px] font-medium text-amber-700">
                Connect and refresh an XPM or FYI platform first to use PracPlanner.
              </p>
            ) : null}
          </div>

          {/* Step 2: Staff Assignment */}
          <div>
            <div className="flex items-center mb-3">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold mr-2.5 shrink-0" style={{ backgroundColor: plannerTheme.primary }}>2</span>
              <span className="text-[15px] font-bold text-gray-900">Staff Assignment Source</span>
            </div>
            <p className="text-[13px] text-gray-400 mb-3 pl-[34px]">Choose where assigned staff should come from for capacity planning.</p>
            <div className="grid grid-cols-3 gap-2.5 pl-[34px]">
              {availableStaffAssignedOptions.map(({ value, label, desc }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => dispatch({ type: 'SET_STAFF_ASSIGNED_DATA_POINT', value })}
                  className={`border-2 rounded-[10px] py-3 px-3 text-center transition-all ${
                    state.staffAssignedDataPoint === value
                      ? ''
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  style={state.staffAssignedDataPoint === value ? { borderColor: plannerTheme.primary, backgroundColor: plannerTheme.soft } : undefined}
                >
                  <div className="text-[13px] font-bold text-gray-900">{label}</div>
                  <div className="text-[11px] text-gray-400 mt-0.5">{desc}</div>
                </button>
              ))}
            </div>
            {state.dataSource && state.staffAssignedDataPoint == null ? (
              <p className="text-[12px] text-red-500 mt-2 pl-[34px]">Select a Staff Assigned Data Point to continue.</p>
            ) : null}
            {staffAssignmentSyncNote ? (
              <div className="pl-[34px]">
                <SyncNote note={staffAssignmentSyncNote} />
              </div>
            ) : null}
          </div>

          {/* Step 3: Budget Mode */}
          <div>
            <div className="flex items-center mb-3">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold mr-2.5 shrink-0" style={{ backgroundColor: plannerTheme.primary }}>3</span>
              <span className="text-[15px] font-bold text-gray-900">Budget Mode</span>
            </div>
            <p className="text-[13px] text-gray-400 mb-3 pl-[34px]">How do you measure capacity - in billable hours or dollar value?</p>
            <div className="flex gap-2.5 pl-[34px]">
              {BUDGET_MODES.map(({ value, label, desc }) => (
                <button
                  key={value}
                  onClick={() => dispatch({ type: 'SET_BUDGET_MODE', value })}
                  className={`flex-1 border-2 rounded-[10px] py-3.5 px-4 text-center transition-all ${
                    state.budgetMode === value
                      ? ''
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  style={state.budgetMode === value ? { borderColor: plannerTheme.primary, backgroundColor: plannerTheme.soft } : undefined}
                >
                  <div className="flex items-center justify-center gap-2 text-[14px] font-bold text-gray-900">
                    <span className={state.budgetMode === value ? 'text-[#38C6BC]' : 'text-gray-500'}>
                      {getBudgetModeIcon(value)}
                    </span>
                    <span>{label}</span>
                  </div>
                  <div className="text-[12px] text-gray-400 mt-0.5">{desc}</div>
                </button>
              ))}
            </div>
            {budgetModeSyncNote ? (
              <div className="pl-[34px]">
                <SyncNote note={budgetModeSyncNote} />
              </div>
            ) : null}
          </div>

          {/* Step 4: Granularity */}
          <div>
            <div className="flex items-center mb-3">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold mr-2.5 shrink-0" style={{ backgroundColor: plannerTheme.primary }}>4</span>
              <span className="text-[15px] font-bold text-gray-900">Default Granularity</span>
            </div>
            <p className="text-[13px] text-gray-400 mb-3 pl-[34px]">Choose how you'd like to view time periods by default. You can change this anytime.</p>
            <div className="flex gap-2.5 pl-[34px]">
              {GRANULARITIES.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => dispatch({ type: 'SET_GRANULARITY', value })}
                  className={`flex-1 border-2 rounded-[10px] py-3 text-center transition-all ${
                    state.granularity === value
                      ? ''
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  style={state.granularity === value ? { borderColor: plannerTheme.primary, backgroundColor: plannerTheme.soft } : undefined}
                >
                  <div className="text-[13px] font-bold text-gray-900">{label}</div>
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="px-10 pb-8 flex justify-between items-center">
          <span className="text-xs text-gray-400">You can update these settings anytime from the gear icon.</span>
          <div className="flex items-center gap-2">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-5 py-2.5 text-[15px] font-semibold text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              disabled={!canSave || saveMutation.isPending}
              onClick={handleLaunch}
              className="px-7 py-2.5 text-[15px] font-semibold text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              style={{ backgroundColor: plannerTheme.primary }}
            >
              {saveMutation.isPending ? 'Saving...' : mode === 'settings' ? 'Save' : 'Launch PracPlanner'}
            </button>
          </div>
        </div>

      </div>
    </FeatureThemeProvider>
  )
}
